import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import ContextualInfo from 'Components/ContextualInfo';
import TestIds from 'Tests/TestIds';
import useTranslate from 'Hooks/useTranslate';
import { userFullName } from 'Tools/LabelFormatter/userFullName';

interface Props {
  owners: readonly IOrganizationOwner[];
}

/** Above this number of owners, only the first ones are shown, followed by an icon revealing the rest. */
const MAX_OWNERS_DISPLAYED = 5;

function OwnerName({ owner }: Readonly<{ owner: IOrganizationOwner }>) {
  const name = userFullName(owner.firstName, owner.lastName);

  return (
    <a
      href={`mailto:${owner.email}`}
      data-testid={TestIds.pages.userProfile.account.organizationOwnerLink}
    >
      {name}
    </a>
  );
}

function OwnerNamesJoined({ owners }: Readonly<{ owners: readonly IOrganizationOwner[] }>) {
  return (
    <>
      {owners.map((owner, index) => (
        <Fragment key={`${owner.firstName}-${owner.lastName}-${index}`}>
          {index > 0 && ', '}
          <OwnerName owner={owner} />
        </Fragment>
      ))}
    </>
  );
}

function OwnerNamesList({ owners }: Readonly<{ owners: readonly IOrganizationOwner[] }>) {
  const visibleOwners = owners.slice(0, MAX_OWNERS_DISPLAYED);

  return (
    <>
      <OwnerNamesJoined owners={visibleOwners} />
      {owners.length > MAX_OWNERS_DISPLAYED && (
        <ContextualInfo
          content={<OwnerNamesJoined owners={owners} />}
          style={{ display: 'inline-flex', verticalAlign: 'middle' }}
        />
      )}
    </>
  );
}
/**
 * Info message displayed when a user's account is managed by their organization's owner(s).
 * Beyond a few owners, the rest are revealed on demand.
 */
function OrganizationOwnersInfo({ owners }: Readonly<Props>) {
  const ts = useTranslate();

  if (owners.length === 0) {
    return <>{ts('page.userProfile.section.account.organizationManagedInfo')}</>;
  }

  if (owners.length === 1) {
    return (
      <FormattedMessage
        id="page.userProfile.section.account.organizationManagedInfoSingleOwner"
        values={{ ownerName: <OwnerName owner={owners[0]} /> }}
      />
    );
  }

  return (
    <FormattedMessage
      id="page.userProfile.section.account.organizationManagedInfoMultipleOwners"
      values={{ ownerNames: <OwnerNamesList owners={owners} /> }}
    />
  );
}

export default OrganizationOwnersInfo;
